import { gql, useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '../assets/i18n/config';
import type {
  DowntimeSchedule,
  FetchApplicableDowntimeScheduleResponse,
  FetchDowntimeSchedulesResponse,
} from '../generated/graphql';
import {
  DowntimeSchedulePlatform,
  DowntimeScheduleState,
} from '../interfaces/downtimeSchedule';

const FETCH_DOWNTIME_SCHEDULES = gql`
  query fetchDowntimeSchedules($input: FetchDowntimeSchedulesInput!) {
    fetchDowntimeSchedules(input: $input) {
      downtimeSchedules {
        id
        startTime
        endTime
        translatedNote { ${TRANSLATED_STRING_FIELDS} }
        excludeRoles
      }
    }
  }
`;

const FETCH_APPLICABLE_DOWNTIME_SCHEDULE = gql`
  query fetchApplicableDowntimeSchedule($platform: Platforms!) {
    fetchApplicableDowntimeSchedule(platform: $platform) {
      downtimeSchedule {
        translatedTitle { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const DEFAULT_POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes

type UseDowntimeSchedulesResult = {
  upcomingDowntimeSchedules: DowntimeSchedule[];
  applicableDowntimeSchedule: DowntimeSchedule | undefined;
};

export const useDowntimeSchedules = (
  organizationId?: string,
  pollInterval = DEFAULT_POLL_INTERVAL,
): UseDowntimeSchedulesResult => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: schedulesData } = useQuery<{
    fetchDowntimeSchedules: FetchDowntimeSchedulesResponse;
  }>(FETCH_DOWNTIME_SCHEDULES, {
    variables: {
      input: {
        filter: {
          organization: organizationId,
          platforms: [DowntimeSchedulePlatform.WEB],
          state: DowntimeScheduleState.ACTIVE,
          applyRoleExclusion: true,
        },
        pagination: {
          sortField: 'startTime',
          sortDesc: false,
        },
      },
    },
    skip: !organizationId,
    fetchPolicy: 'network-only',
  });

  const { data: applicableData, refetch } = useQuery<{
    fetchApplicableDowntimeSchedule: FetchApplicableDowntimeScheduleResponse;
  }>(FETCH_APPLICABLE_DOWNTIME_SCHEDULE, {
    variables: { platform: DowntimeSchedulePlatform.WEB },
    pollInterval,
    fetchPolicy: 'network-only',
  });

  const refetchRef = useRef(refetch);

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const schedules =
    schedulesData?.fetchDowntimeSchedules?.downtimeSchedules ?? [];

  // Stable key so the timer only resets when the actual start times change,
  // not when Apollo hands back a new refetch identity.
  const scheduleStartTimesKey = useMemo(
    () => schedules.map((s) => s.startTime).join(','),
    [schedules],
  );

  useEffect(() => {
    if (!scheduleStartTimesKey) return;

    const now = Date.now();
    const nearestStart = scheduleStartTimesKey
      .split(',')
      .map((t) => new Date(t).getTime())
      .filter((t) => t > now)[0];

    if (!nearestStart) return;

    timerRef.current = setTimeout(() => {
      refetchRef.current();
    }, nearestStart - now);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [scheduleStartTimesKey]);

  const upcomingDowntimeSchedules = schedules.filter((s: DowntimeSchedule) => {
    const startTime = dayjs(s.startTime);
    const now = dayjs();
    return (
      startTime.isAfter(now) &&
      startTime.diff(now, 'h') < 72 &&
      !!translateBackend(s.translatedNote)?.trim()
    );
  });

  const applicableDowntimeSchedule =
    applicableData?.fetchApplicableDowntimeSchedule?.downtimeSchedule ??
    undefined;

  return { upcomingDowntimeSchedules, applicableDowntimeSchedule };
};
